import React, { useState } from 'react';
import { AdSettings } from '../types';
import { Tv, Smartphone, ScrollText, Monitor, EyeOff, Gauge, ChevronDown, ChevronUp, SlidersHorizontal, Clock } from 'lucide-react';

interface AdManagerSettingsProps {
    settings: AdSettings;
    updateSetting: (key: keyof AdSettings, value: any) => void;
    primaryColor: string;
}

const ToggleCard: React.FC<{
    label: string;
    description: string;
    icon: React.ElementType;
    enabled: boolean;
    onToggle: () => void;
    color: string;
}> = ({ label, description, icon: Icon, enabled, onToggle, color }) => (
    <div className="bg-white rounded-xl border border-slate-100 p-4 flex items-center justify-between hover:border-slate-200 transition-colors">
        <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg" style={{ backgroundColor: enabled ? `${color}10` : '#f1f5f9' }}>
                <Icon className="w-4 h-4" style={{ color: enabled ? color : '#94a3b8' }} />
            </div>
            <div>
                <p className="font-bold text-xs text-slate-700">{label}</p>
                <p className="text-[10px] text-slate-400">{description}</p>
            </div>
        </div>
        <button
            onClick={onToggle}
            className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none`}
            style={{ backgroundColor: enabled ? color : '#cbd5e1' }}
            role="switch"
            aria-checked={enabled}
        >
            <span
                aria-hidden="true"
                className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-md ring-0 transition duration-300 ease-in-out ${enabled ? 'translate-x-5' : 'translate-x-0'}`}
            />
        </button>
    </div>
);

const AdManagerSettings: React.FC<AdManagerSettingsProps> = ({ settings, updateSetting, primaryColor }) => {
    const [isOpen, setIsOpen] = useState(false);

    return (
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="w-full flex items-center justify-between p-4 bg-slate-50 hover:bg-slate-100 transition-colors"
            >
                <div className="flex items-center gap-2">
                    <SlidersHorizontal className="w-4 h-4 text-slate-500" />
                    <span className="font-bold text-sm text-slate-700">Ajustes Avanzados</span>
                </div>
                {isOpen ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
            </button>

            {isOpen && (
                <div className="p-4 border-t border-slate-100">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <ToggleCard
                            label="Publicidad en TVs"
                            description="Banners y ticker en pantallas grandes"
                            icon={Tv}
                            enabled={settings.showAdsOnTV}
                            onToggle={() => updateSetting('showAdsOnTV', !settings.showAdsOnTV)}
                            color={primaryColor}
                        />

                        <ToggleCard
                            label="Publicidad en Celulares"
                            description="Banners sutiles en vista familiar"
                            icon={Smartphone}
                            enabled={settings.showAdsOnMobile}
                            onToggle={() => updateSetting('showAdsOnMobile', !settings.showAdsOnMobile)}
                            color={primaryColor}
                        />

                        <ToggleCard
                            label="Ticker (Texto Corriendo)"
                            description="Mensajes promocionales en la barra inferior"
                            icon={ScrollText}
                            enabled={settings.tickerEnabled}
                            onToggle={() => updateSetting('tickerEnabled', !settings.tickerEnabled)}
                            color={primaryColor}
                        />

                        <ToggleCard
                            label="Fullscreen sin Pacientes"
                            description="Slideshow de slides cuando la TV vacía"
                            icon={Monitor}
                            enabled={settings.fullscreenWhenEmpty}
                            onToggle={() => updateSetting('fullscreenWhenEmpty', !settings.fullscreenWhenEmpty)}
                            color={primaryColor}
                        />

                        <ToggleCard
                            label="Ocultar en Cirugía Activa"
                            description="No mostrar ads en celulares durante cirugía"
                            icon={EyeOff}
                            enabled={settings.hideAdsDuringSurgery}
                            onToggle={() => updateSetting('hideAdsDuringSurgery', !settings.hideAdsDuringSurgery)}
                            color={primaryColor}
                        />

                        {/* Ticker Speed */}
                        <div className="bg-white rounded-xl border border-slate-100 p-4 flex items-center justify-between hover:border-slate-200 transition-colors">
                            <div className="flex items-center gap-3">
                                <div className="p-2 rounded-lg bg-slate-50">
                                    <Gauge className="w-4 h-4 text-slate-500" />
                                </div>
                                <div>
                                    <p className="font-bold text-xs text-slate-700">Velocidad Ticker</p>
                                    <p className="text-[10px] text-slate-400">Qué tan rápido corre</p>
                                </div>
                            </div>
                            <select
                                value={settings.tickerSpeed}
                                onChange={(e) => updateSetting('tickerSpeed', e.target.value)}
                                className="text-xs font-bold border border-slate-200 rounded-lg px-3 py-1.5 bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            >
                                <option value="slow">Lento</option>
                                <option value="normal">Normal</option>
                                <option value="fast">Rápido</option>
                            </select>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdManagerSettings;
